'use client'

import { useSyncExternalStore } from 'react'

import { uniqBy } from 'lodash'
import { Connector, useConfig, useConnectors } from 'wagmi'

// Privy syncs embedded wallets into wagmi with id `<meta.id>.<walletAddress>`.
const PRIVY_SYNCED_CONNECTOR_ID_PATTERN = /\.0x[0-9a-fA-F]{40}$/

export const isPrivySyncedConnector = (connector: Connector): boolean =>
  PRIVY_SYNCED_CONNECTOR_ID_PATTERN.test(connector.id)

export type DetectedWallet = {
  rdns: string
  name: string
  icon: string
  connector: Connector
}

const EMPTY_PROVIDERS: never[] = []
const getEmptyProviders = () => EMPTY_PROVIDERS
const noopSubscribe = () => () => undefined

export const findConnectorForRdns = (
  connectors: readonly Connector[],
  rdns: string,
): Connector | undefined =>
  connectors.find((connector) => connector.id === rdns) ??
  connectors.find((connector) => {
    const claimed =
      typeof connector.rdns === 'string'
        ? [connector.rdns]
        : (connector.rdns ?? [])
    return claimed.includes(rdns)
  })

export const useDetectedWallets = (): DetectedWallet[] => {
  const connectors = useConnectors()
  // Wagmi's MIPD store retains announcements that its connector list drops
  // when a configured connector claims the same rdns.
  const mipd = useConfig()._internal.mipd
  const providers = useSyncExternalStore(
    mipd?.subscribe ?? noopSubscribe,
    mipd?.getProviders ?? getEmptyProviders,
    getEmptyProviders,
  )

  return uniqBy(providers, ({ info }) => info.rdns).flatMap(({ info }) => {
    if (!info.rdns || !info.name || !info.icon) return []
    const connector = findConnectorForRdns(connectors, info.rdns)
    return connector ? [{ ...info, connector }] : []
  })
}
